/**
 * useMessageAdjustments.js
 *
 * 메시지 유형별 전환 보정치 관리 컴포저블
 * SOLID 원칙 준수:
 * - S: 메시지 보정치 관리만 담당
 * - O: 새 메시지 타입 추가 시 config만 확장
 * - L: 모든 메시지 타입에 동일 인터페이스 제공
 * - I: 보정치 관리 로직만 분리
 * - D: 컴포넌트는 이 컴포저블에만 의존
 */

import { reactive, ref, computed } from 'vue';
import { message } from 'ant-design-vue';

/**
 * 메시지 유형별 보정치 관리 설정
 * 새로운 메시지 타입 추가 시 여기에만 추가하면 됨
 */
export const MESSAGE_TYPE_CONFIG = {
  certificate: {
    label: '인증서 알림톡',
    fields: ['sent', 'uniqueClicks', 'clicks', 'eventPageVisits', 'shares']
  },
  review: {
    label: '후기 알림톡',
    fields: ['sent', 'uniqueClicks', 'clicks', 'reviewSubmissions', 'shares']
  },
  friend: {
    label: '병원예약 유도 친구톡',
    fields: ['sent', 'quizParticipants', 'hospitalSelections', 'customerInfoInputs', 'reservations']
  }
};

/**
 * 보정 가능한 필드 목록 및 라벨
 */
export const ADJUSTABLE_FIELDS = {
  sent: { label: '발송건수' },
  uniqueClicks: { label: '순클릭수' },
  clicks: { label: '클릭수' },
  eventPageVisits: { label: '이벤트페이지유입수' },
  shares: { label: '공유수' },
  reviewSubmissions: { label: '후기작성수' },
  quizParticipants: { label: '퀴즈참여수' },
  hospitalSelections: { label: '병원선택수' },
  customerInfoInputs: { label: '고객정보입력수' },
  reservations: { label: '병원예약수' }
};

/**
 * useMessageAdjustments 컴포저블
 *
 * @param {Object} api - API 인스턴스
 * @param {Function} api.updateAdjustments - 보정치 업데이트 함수
 * @param {Function} api.getAdjustments - 보정치 조회 함수
 */
export function useMessageAdjustments(api) {
  // 메시지 유형별 보정치 저장 상태
  // 구조: { messageType: { date: { field: value } } }
  const messageAdjustments = reactive({
    certificate: {},
    review: {},
    friend: {}
  });

  // 편집 상태 관리
  const editingCell = ref(null); // { messageType, date, field }
  const tempInputValue = ref(0);
  const loading = ref(false);

  /**
   * 현재 reportId (외부에서 설정)
   */
  const reportId = ref(null);

  /**
   * reportId 설정
   * @param {string} id
   */
  const setReportId = (id) => {
    reportId.value = id;
  };

  /**
   * 보정치 키 생성 (백엔드 저장용)
   * @param {string} messageType
   * @param {string} date
   * @param {string} field
   * @returns {string}
   */
  const getAdjustmentKey = (messageType, date, field) => {
    return `messageConversion.${messageType}.${date}.${field}`;
  };

  /**
   * 보정치 조회 - DB에서 저장된 보정치 로드
   * @param {string} currentReportId
   */
  const loadMessageAdjustments = (currentReportId) => {
    if (!currentReportId) return;

    reportId.value = currentReportId;
    loading.value = true;

    api.getAdjustments(
      { reportId: currentReportId },
      (response) => {
        const adjustmentsData = response?.data || response?.adjustments || response || {};

        // messageConversion 데이터 파싱
        const messageConversion = adjustmentsData.messageConversion || {};

        Object.keys(MESSAGE_TYPE_CONFIG).forEach((msgType) => {
          if (messageConversion[msgType]) {
            messageAdjustments[msgType] = {};

            Object.entries(messageConversion[msgType]).forEach(([date, fields]) => {
              messageAdjustments[msgType][date] = {};

              Object.entries(fields).forEach(([field, data]) => {
                // data가 객체({ value: n })인 경우와 숫자인 경우 모두 처리
                const value = typeof data === 'object' ? (data.value || 0) : (data || 0);
                messageAdjustments[msgType][date][field] = value;
              });
            });
          }
        });

        loading.value = false;
      },
      (error) => {
        console.error('보정치 로드 실패:', error);
        loading.value = false;
      }
    );
  };

  /**
   * 특정 셀의 보정치 가져오기
   * @param {string} messageType
   * @param {string} date
   * @param {string} field
   * @returns {number}
   */
  const getAdjustment = (messageType, date, field) => {
    return messageAdjustments[messageType]?.[date]?.[field] || 0;
  };

  /**
   * 보정치가 있는지 확인
   * @param {string} messageType
   * @param {string} date
   * @param {string} field
   * @returns {boolean}
   */
  const hasAdjustment = (messageType, date, field) => {
    const value = getAdjustment(messageType, date, field);
    return value !== 0;
  };

  /**
   * 보정된 값 계산
   * @param {number} baseValue - 원본 값
   * @param {string} messageType
   * @param {string} date
   * @param {string} field
   * @returns {number}
   */
  const getAdjustedValue = (baseValue, messageType, date, field) => {
    const adjustment = getAdjustment(messageType, date, field);
    return (baseValue || 0) + adjustment;
  };

  /**
   * 편집 시작
   * @param {string} messageType
   * @param {string} date
   * @param {string} field
   */
  const startEditing = (messageType, date, field) => {
    editingCell.value = { messageType, date, field };
    tempInputValue.value = getAdjustment(messageType, date, field);
  };

  /**
   * 편집 취소
   */
  const cancelEdit = () => {
    editingCell.value = null;
    tempInputValue.value = 0;
  };

  /**
   * 편집 확인 (저장)
   */
  const confirmEdit = () => {
    if (!editingCell.value) return;

    const { messageType, date, field } = editingCell.value;
    const newValue = tempInputValue.value || 0;

    // reportId가 없으면 로컬에만 저장
    if (!reportId.value) {
      setLocalAdjustment(messageType, date, field, newValue);
      editingCell.value = null;
      tempInputValue.value = 0;
      return;
    }

    // 백엔드에 저장
    const adjustmentKey = getAdjustmentKey(messageType, date, field);

    api.updateAdjustments(
      {
        reportId: reportId.value,
        adjustments: { [adjustmentKey]: newValue }
      },
      () => {
        setLocalAdjustment(messageType, date, field, newValue);
        editingCell.value = null;
        tempInputValue.value = 0;
        message.success('보정치가 저장되었습니다.');
      },
      (error) => {
        console.error('보정치 저장 실패:', error);
        message.error('보정치 저장에 실패했습니다.');
      }
    );
  };

  /**
   * 로컬 상태에 보정치 설정
   * @param {string} messageType
   * @param {string} date
   * @param {string} field
   * @param {number} value
   */
  const setLocalAdjustment = (messageType, date, field, value) => {
    if (!messageAdjustments[messageType]) {
      messageAdjustments[messageType] = {};
    }
    if (!messageAdjustments[messageType][date]) {
      messageAdjustments[messageType][date] = {};
    }
    messageAdjustments[messageType][date][field] = value;
  };

  /**
   * 키보드 이벤트 핸들러
   * @param {KeyboardEvent} event
   */
  const handleKeydown = (event) => {
    if (event.key === 'Enter') {
      confirmEdit();
    } else if (event.key === 'Escape') {
      cancelEdit();
    }
  };

  /**
   * 현재 편집 중인 셀인지 확인
   * @param {string} messageType
   * @param {string} date
   * @param {string} field
   * @returns {boolean}
   */
  const isEditing = (messageType, date, field) => {
    if (!editingCell.value) return false;
    return (
      editingCell.value.messageType === messageType &&
      editingCell.value.date === date &&
      editingCell.value.field === field
    );
  };

  /**
   * 데이터 배열의 특정 필드 보정 합계 계산
   * @param {Array} data
   * @param {string} messageType
   * @param {string} field
   * @returns {number}
   */
  const calculateAdjustedSum = (data, messageType, field) => {
    if (!data || data.length === 0) return 0;

    return data.reduce((sum, item) => {
      const baseValue = item[field] || 0;
      const adjustment = getAdjustment(messageType, item.date, field);
      return sum + baseValue + adjustment;
    }, 0);
  };

  /**
   * 모든 보정치 초기화
   */
  const resetAdjustments = () => {
    Object.keys(messageAdjustments).forEach((msgType) => {
      messageAdjustments[msgType] = {};
    });
    editingCell.value = null;
    tempInputValue.value = 0;
  };

  return {
    // 상태
    messageAdjustments,
    editingCell,
    tempInputValue,
    loading,
    reportId,

    // 설정
    MESSAGE_TYPE_CONFIG,
    ADJUSTABLE_FIELDS,

    // 메서드
    setReportId,
    loadMessageAdjustments,
    getAdjustment,
    hasAdjustment,
    getAdjustedValue,
    startEditing,
    cancelEdit,
    confirmEdit,
    handleKeydown,
    isEditing,
    calculateAdjustedSum,
    resetAdjustments
  };
}

export default useMessageAdjustments;
